<template>
    <BaseLayout :heading="'Verbal Fluency - Supermarket - Interview'" />

    <div class="instructions">
        <div class="line">Next I'd like you to name all the things you can find or buy in a supermarket. You will have one minute to name as many different items as fast as you can. Ready? Press continue and begin.</div>
        <div class="line">(Wait for the time's up message after one minute. If there has been a 15 second pause with no response, say "keep going, anything at the supermarket")</div>
        <div class="line">That is fine. <b>Press the continue button.</b></div>
    </div>

    <VerbalFluencyInterview
      :jsonData="jsonData"
      :audioFile="audioFile"
      @update:enteredWord="updateEnteredWord"
      @update:selectedAnswer="updateSelectedAnswer"
      @entrySummaryUpdated="updateEntrySummary"
      @update:suggestedWords="updateSuggestedWords"
    />

    <NotesSection 
      :administeringNotes="jsonData ? jsonData.administeringNotes : ''" 
      :scoringNotes="jsonData ? jsonData.scoringNotes : ''" 
      ref="notesSection" />

    <TextButton buttonText="Submit" :dark="true" type="submit" @click="submitForm" />

    <NotificationPopup 
      :message="notificationMessage" 
      :visible="notificationVisible"
    />

    <ConfirmationPopup
      :visible="showConfirmPopup"
      @confirm="handleConfirmLeave"
      @noSave="handleNoSaveLeave"
      @cancel="showConfirmPopup = false"
    />

    <div v-if="loading" class="audio-status">Loading audio recording...</div>
    
    <div v-if="!loading" class="audio-player-container">
      <div v-if="error" class="audio-status error">{{ error }}</div>
      <div v-else-if="!audioUrl" class="audio-status">No audio recording available for this participant.</div>
      
      <AudioPlayer 
        v-else
        :audioUrl="audioUrl" 
        taskTitle="Supermarket"
        @close="closeAudioPlayer"
      />
    </div>
</template>

<script>
import BaseLayout from '../BaseLayout.vue';
import VerbalFluencyInterview from '@/components/Pages/VerbalFluencyInterview.vue';
import TaskController from '../../controllers/TaskController';
import ServerCodeModel from '../../models/ServerCodeModel';
import NotificationPopup from '@/components/NotificationPopup.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import NotesSection from '@/components/NotesSection.vue';
import TextButton from '@/components/TextButton.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
import ACTApiService from '@/services/ACTApiService.js';

export default {
  components: {
    BaseLayout,
    VerbalFluencyInterview,
    NotificationPopup,
    ConfirmationPopup,
    NotesSection,
    TextButton,
    AudioPlayer
  },
  data() {
    return {
      controller: null,
      jsonData: null,
      notificationMessage: '',
      notificationVisible: false,
      hasUnsavedChanges: false,
      showConfirmPopup: false,
      nextRoute: null,
      forceNavigate: false,
      audioUrl: null,
      audioFile: null,
      loading: false,
      error: null,
      actApiService: new ACTApiService(),
      transcriptWords: [],
      loadingTranscript: false,
      transcriptError: null
    };
  },
  computed: {
    isAdministering() {
        return this.$route.query.selection === 'administer';
    }
  },
  async mounted() {
    const serverCodeModel = new ServerCodeModel();
    serverCodeModel.logType = 'InterviewerVerbalFluencySupermarketLog';

    this.controller = new TaskController(serverCodeModel.logType, 'verbalFluencySupermarketData', serverCodeModel);

    await this.loadData();
    
    await this.fetchLogAudio();
    
    await this.fetchTranscript();
    
    if (this.jsonData.timeCreated === '') {
      this.jsonData.timeCreated = new Date().toUTCString();
    }
    this.jsonData.timeLastModified = new Date().toUTCString();
  },
  methods: {
    async loadData() {
      await this.controller.loadData();
      this.jsonData = this.controller.model.data;
      this.hasUnsavedChanges = false;
    },
    updateEnteredWord(index, value) {
      this.jsonData.entries[index].enteredWord = value;
      this.hasUnsavedChanges = true;
    },
    updateSelectedAnswer(index, value) {
      this.jsonData.entries[index].selectedAnswer = value;
      this.hasUnsavedChanges = true;
    },
    async submitForm() {
      this.notificationMessage = 'Submitting...';
      this.notificationVisible = true;
      
      // Set timestamps
      if (this.jsonData.timeCreated === '') {
        this.jsonData.timeCreated = new Date().toUTCString();
      }
      this.jsonData.timeLastModified = new Date().toUTCString();
      
      // Get notes from NotesSection component
      const notesSection = this.$refs.notesSection;
      const administeringNotes = notesSection.getAdministeringNotes();
      const scoringNotes = notesSection.getScoringNotes();

      if (this.jsonData) {
        this.jsonData.administeringNotes = administeringNotes || '';
        this.jsonData.scoringNotes = scoringNotes || '';
      }
      
      // Add the entry summary to the jsonData
      this.jsonData.entrySummary = this.calculateEntrySummary();
      
      // Log the data we're about to save
      console.log('Saving Supermarket data:', this.jsonData);

      try {
        // Save data to model
        await this.controller.model.saveData(this.jsonData);
        
        // Also save to localStorage directly to ensure it's there
        const subjectId = localStorage.getItem('currentSubjectId');
        if (subjectId) {
          const allData = JSON.parse(localStorage.getItem('subjectData') || '{}');
          if (!allData[subjectId]) {
            allData[subjectId] = {};
          }
          
          // Save under both possible keys to ensure it's found
          allData[subjectId].supermarketData = this.jsonData;
          allData[subjectId].verbalFluencySupermarketData = this.jsonData;
          
          localStorage.setItem('subjectData', JSON.stringify(allData));
        }
        
        // Submit form
        await this.controller.submitForm();
        
        this.notificationMessage = 'Submitted Successfully!';
        this.hasUnsavedChanges = false;
      } catch (error) {
        this.notificationMessage = 'Submitting failed. Please try again.';
        console.error('Error submitting form:', error);
      }
      finally {
        this.resetNotification();
      }
    },
    calculateEntrySummary() {
      const summary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
      
      if (this.jsonData && this.jsonData.entries) {
        this.jsonData.entries.forEach(entry => {
          if (entry.selectedAnswer) {
            const key = entry.selectedAnswer.toLowerCase();
            if (Object.prototype.hasOwnProperty.call(summary, key)) {
              summary[key]++;
            }
          }
        });
      }
      
      console.log('Calculated summary:', summary);
      return summary;
    },
    resetNotification() {
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async handleConfirmLeave() {
      this.showConfirmPopup = false;
      this.hasUnsavedChanges = false;
      await this.submitForm();
      this.$router.push(this.nextRoute);
    },
    handleNoSaveLeave() {
      this.showConfirmPopup = false;
      this.forceNavigate = true;
      this.hasUnsavedChanges = false;
      this.$router.push(this.nextRoute);
    },
    updateEntrySummary(summary) {
      if (this.jsonData) {
        this.jsonData.entrySummary = summary;
        this.hasUnsavedChanges = true;
      }
    },
    async fetchLogAudio() {
      this.loading = true;
      this.error = null;
      
      const logId = localStorage.getItem('verbalFluencySupermarketLogId');
      
      if (!logId) {
        this.loading = false;
        console.log('No Verbal Fluency Supermarket audio log ID found');
        return;
      }
      
      try {
        console.log('Fetching Supermarket audio from log ID:', logId);
        const audioBlob = await this.actApiService.getLogBinary(logId);
        
        if (!audioBlob || audioBlob.size === 0) {
          throw new Error('Audio recording is empty or not available');
        }
        
        this.audioUrl = URL.createObjectURL(audioBlob);
        console.log('Created audio URL from log:', this.audioUrl);
      } catch (error) {
        console.error('Error fetching Supermarket audio from logs:', error);
        this.error = 'No audio recording found for this participant.';
        this.audioUrl = null;
      } finally {
        this.loading = false;
      }
    },
    closeAudioPlayer() {
      this.audioUrl = null;
    },
    updateSuggestedWords(words) {
      if (this.jsonData) {
        // Create a new object to avoid direct mutation
        this.jsonData = {
          ...this.jsonData,
          suggestedWords: words
        };
        
        // Save the data to ensure suggestions are persisted
        this.controller.model.saveData(this.jsonData);
        console.log('Saved suggested words to data:', words);
      }
    },
    async fetchTranscript() {
      this.loadingTranscript = true;
      this.transcriptError = null;
      
      const logId = localStorage.getItem('verbalFluencySupermarketLogId');
      
      if (!logId) {
        this.loadingTranscript = false;
        console.log('No Verbal Fluency Supermarket log ID found for transcript');
        return;
      }
      
      try {
        console.log('Fetching Supermarket transcript from log ID:', logId);
        const response = await this.actApiService.getLogTranscript(logId);
        
        if (response && response.Value && response.Value.Words) {
          this.transcriptWords = response.Value.Words
            .map(item => item.Word)
            .filter(word => word.length >= 3) // Filter out very short words
            .filter(word => !this.isCommonWord(word)); // Filter out common words
          
          console.log('Fetched potential supermarket items from transcript:', this.transcriptWords);
          
          // Update the suggestions in the component
          if (this.transcriptWords.length > 0) {
            this.updateSuggestedWords(this.transcriptWords);
          }
        } else {
          console.log('Transcript data does not contain expected Word structure');
        }
      } catch (error) {
        console.error('Error fetching Supermarket transcript:', error);
        this.transcriptError = 'Failed to load word suggestions.';
      } finally {
        this.loadingTranscript = false;
      }
    },
    isCommonWord(word) {
      const commonWords = [
        'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you', 'your', 'yours',
        'yourself', 'yourselves', 'he', 'him', 'his', 'himself', 'she', 'her', 'hers',
        'herself', 'it', 'its', 'itself', 'they', 'them', 'their', 'theirs', 'themselves',
        'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those', 'am', 'is', 'are',
        'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does',
        'did', 'doing', 'a', 'an', 'the', 'and', 'but', 'if', 'or', 'because', 'as', 'until',
        'while', 'of', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into',
        'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down',
        'in', 'out', 'on', 'off', 'over', 'under', 'again', 'further', 'then', 'once', 'here',
        'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more',
        'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so',
        'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don', 'don\'t', 'should',
        'should\'ve', 'now', 'd', 'll', 'm', 'o', 're', 've', 'y', 'ain', 'aren', 'aren\'t',
        'couldn', 'couldn\'t', 'didn', 'didn\'t', 'doesn', 'doesn\'t', 'hadn', 'hadn\'t',
        'hasn', 'hasn\'t', 'haven', 'haven\'t', 'isn', 'isn\'t', 'ma', 'mightn', 'mightn\'t',
        'mustn', 'mustn\'t', 'needn', 'needn\'t', 'shan', 'shan\'t', 'shouldn', 'shouldn\'t',
        'wasn', 'wasn\'t', 'weren', 'weren\'t', 'won', 'won\'t', 'wouldn', 'wouldn\'t',
        'go', 'going', 'know', 'right', 'think', 'oh', 'okay', 'said', 'say', 'saying',
        'yeah', 'yes', 'come', 'like', 'um', 'uh', 'would', 'time', 'continue', 'ahead',
        'hit', 'supermarket', 'buy', 'get', 'things', 'items', 'let\'s', 'see'
      ];
      
      // Check if the word is in the common words list
      return commonWords.includes(word.toLowerCase());
    }
  },
  beforeRouteLeave(to, from, next) {
    const notesSection = this.$refs.notesSection;
    const currentAdministeringNotes = notesSection.getAdministeringNotes();
    const currentScoringNotes = notesSection.getScoringNotes();

    if (this.forceNavigate) {
      this.forceNavigate = false;
      this.hasUnsavedChanges = false;
      next();
      return;
    }

    if ( 
      (this.isAdministering && currentAdministeringNotes !== this.jsonData.administeringNotes) || 
      (!this.isAdministering && currentScoringNotes !== this.jsonData.scoringNotes) 
    ) {
      this.hasUnsavedChanges = true;
    }

    if (this.hasUnsavedChanges) {
      this.showConfirmPopup = true;
      this.nextRoute = to.fullPath;
      next(false);
    } else {
      next();
    }
  },
  beforeUnmount() {
    // Clean up object URL to avoid memory leaks
    if (this.audioUrl && this.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.audioUrl);
    }
  }
};
</script>

<style scoped>
.instructions {
    font-size: 18px;
}
.line {
    margin-bottom: 20px;
}
.audio-status {
  margin: 20px 0;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  text-align: center;
  font-style: italic;
}
.audio-status.error {
  background-color: #ffebee;
  color: #c62828;
}
.audio-player-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #f5f5f5;
    padding: 15px;
    box-shadow: 0 -2px 4px rgba(0,0,0,0.1);
    z-index: 1000;
}
audio {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    display: block;
}
/* Commented out since we're not showing the recorder anymore
.audio-section {
    margin-top: 20px;
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f5f5f5;
    border-radius: 4px;
}
.audio-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: 600;
}
*/
</style>